import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/entwicklung-digitaler-dienste")({
  head: () => {
    const h = pageHead("digitalDevelopment", "de");
    return { meta: h.meta, links: h.links };
  },
  component: DigitalePage,
});

const steps = [
  { num: "01", title: "Problem & Konzept", body: "Wir klären Anforderungen, Nutzer und Prozesse — und definieren, was die Anwendung leisten muss und was bewusst nicht." },
  { num: "02", title: "Architektur & Datenmodell", body: "Wir legen das Fundament: Architektur, Datenmodelle und Schnittstellen, die langfristig tragen und erweiterbar bleiben." },
  { num: "03", title: "Entwicklung & Integration", body: "Umsetzung auf React-Basis — als PWA, wrappbar zur nativen App, mit Anbindung an bestehende Systeme und Datenquellen." },
  { num: "04", title: "Betrieb & Weiterentwicklung", body: "Nach dem Go-live begleiten wir Betrieb, Skalierung und kontinuierliche Weiterentwicklung — die Anwendung wächst mit dem Geschäft." },
];

const kinds = [
  { title: "Progressive Web Apps (PWA)", body: "Anwendungen, die im Browser wie native Apps funktionieren — installierbar, offline-fähig, schnell. Eine Codebasis für alle Plattformen." },
  { title: "Native Apps via Wrapping", body: "Wenn App-Store-Präsenz oder native Funktionen gefragt sind, wrappen wir die PWA zur nativen App für iOS und Android — ohne zwei getrennte Apps pflegen zu müssen." },
  { title: "SaaS & Custom-Software", body: "Maßgeschneiderte Anwendungen mit eigener Logik: interne Tools, Plattformen, Portale oder vermarktbare SaaS-Produkte — auf moderner React-Basis." },
];

const capabilities = [
  { name: "Architektur & Datenmodelle", detail: "langfristig tragend, erweiterbar" },
  { name: "PWA-Entwicklung", detail: "installierbar, offline-fähig, schnell" },
  { name: "Native Wrapping", detail: "iOS und Android aus einer Codebasis" },
  { name: "SaaS-Entwicklung", detail: "vermarktbare Produkte mit eigener Logik" },
  { name: "Schnittstellen & APIs", detail: "Anbindung bestehender Systeme" },
  { name: "Frontend auf React-Basis", detail: "modern, performant, unabhängig" },
  { name: "Headless-Content mit Sanity", detail: "wo Inhalte strukturiert einfließen" },
  { name: "Betrieb & Weiterentwicklung", detail: "partnerschaftlich, langfristig" },
];

const stack = [
  { name: "React", small: "Frontend-Fundament" },
  { name: "PWA-Technologien", small: "plattformübergreifend" },
  { name: "Sanity", small: "Content-Layer, wo nötig" },
];

function DigitalePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Entwicklung digitaler Dienste"
          headline={<>Wenn Standard nicht mehr reicht: <em>individuelle Anwendungen,</em> die tragen.</>}
          lead="Manche Aufgaben lassen sich nicht mit einem CMS oder Shop-System lösen. Wir entwickeln individuelle digitale Dienste — Apps auf Basis von Progressive Web Apps, die sich zu nativen Apps wrappen lassen, sowie maßgeschneiderte SaaS- und Custom-Software auf React-Basis."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Entwicklung digitaler Dienste" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Individualsoftware ist <em>kein Wegwerf-Projekt.</em> Sie ist Infrastruktur.</>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Sie brauchen eine klassische Website oder einen Onlineshop? Das sind eigene Disziplinen —{" "}
              <a href="/leistungen/website-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">Website-Entwicklung →</a>
              {" · "}
              <a href="/leistungen/onlineshop-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">Onlineshop-Entwicklung →</a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wenn ein Prozess, ein Produkt oder ein Geschäftsmodell digital wird, entsteht Software, die jeden Tag laufen muss. Sie ist keine Kampagne und keine Visitenkarte — sie ist Infrastruktur. Genau deshalb entscheidet hier nicht das schnelle Ergebnis, sondern ein Fundament, das trägt: eine saubere Architektur, durchdachte Datenmodelle und Code, der auch in zwei Jahren noch erweiterbar ist.</p>
              <p className="mb-5">In der Praxis begegnen uns immer wieder dieselben Muster: zusammengeklickte Tools, die an ihre Grenzen stoßen, Insellösungen ohne gemeinsame Datenbasis, Prototypen, die im Produktivbetrieb zur Altlast werden, und Anwendungen, deren Weiterentwicklung mit jedem Monat teurer wird. Das Ergebnis: Die Software bremst genau das Geschäft, das sie beschleunigen sollte.</p>
              <p>Wir entwickeln digitale Dienste als langfristige Infrastruktur. Am Anfang steht das Problem, nicht die Technologie: Welcher Prozess soll abgebildet werden, welche Nutzer arbeiten damit, welche Systeme müssen angebunden sein, und wo muss die Anwendung in Zukunft hinwachsen können.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="03" kicker="Was wir bauen" headline={<>Drei Arten <em>digitaler Dienste.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {kinds.map((k) => (
                <div key={k.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{k.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{k.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="04" kicker="Vorgehen" headline={<>Vier Phasen vom <em>Problem</em> zur tragfähigen Anwendung.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="05" kicker="PWA-First" headline={<>Eine Codebasis. <em>Überall lauffähig.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-8">Statt von Anfang an zwei oder drei getrennte Apps zu bauen, setzen wir auf einen PWA-First-Ansatz: Die Anwendung entsteht als Progressive Web App und läuft sofort im Browser — installierbar, offline-fähig, schnell. Wird native Präsenz im App Store oder Zugriff auf native Gerätefunktionen gebraucht, wrappen wir dieselbe Anwendung zur nativen App.</p>
            </div>
            <div className="mt-2 rounded-lg border-2 border-lime bg-white p-7">
              <h3 className="mb-4 font-display text-xl text-ink">Was das konkret bringt</h3>
              <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                <li>eine Codebasis statt drei</li>
                <li>sofort lauffähig im Browser, ohne App-Store-Freigabe</li>
                <li>native Apps für iOS und Android bei Bedarf</li>
                <li>deutlich geringere Pflege- und Weiterentwicklungskosten</li>
                <li>volle gestalterische und funktionale Freiheit</li>
              </ul>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Capabilities & Stack" headline={<>Womit wir digitale Dienste <em>tragfähig</em> machen.</>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="07" />

        <RelatedServices
          locale="de"
          fromId="entwicklung-digitaler-dienste"
          number="08"
          items={relatedBySource["entwicklung-digitaler-dienste"]}
        />

        <Section number="09" kicker="Engagement">
          <CTABlock
            headline={<>Eine Anwendung, die <em>trägt</em> — nicht zur Altlast wird</>}
            body="Ob PWA, native App oder maßgeschneidertes SaaS — wir klären zuerst, was das Problem wirklich verlangt. Wenn Sie eine Anwendung brauchen, die sauber gebaut ist, mitwächst und langfristig trägt, sprechen wir gerne darüber."
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
        <GlossaryTerms locale="de" ids={["server-side-rendering", "single-page-application", "tanstack-start", "hydration", "core-web-vitals"]} />
      </main>
      <Footer />
    </>
  );
}
